import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import { useAuth } from './authContext';

const EventImagesContext = createContext();

export const useEventImages = () => {
  const context = useContext(EventImagesContext);
  if (!context) {
    throw new Error('useEventImages must be used within an EventImagesProvider');
  }
  return context;
};

export const EventImagesProvider = ({ children }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const { makeAuthenticatedRequest, loginType } = useAuth();

  // API base URL for event images endpoints
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://7d515f15e81f.ngrok-free.app';

  // Determine the correct base URL based on login type
  const getEventImagesBaseUrl = useCallback(() => {
    if (loginType === 'master') {
      return `${API_BASE_URL}/master/event-images/api/v1/events`;
    } else if (loginType === 'admin') {
      return `${API_BASE_URL}/admin/event-images/api/v1/events`;
    } else {
      throw new Error('Invalid login type for event images access');
    }
  }, [API_BASE_URL, loginType]);

  // Helper function to handle API responses
  const handleApiResponse = async (response) => {
    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || data.message || 'Request failed');
    }

    return data;
  };

  // Clear error state
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Validate user access
  const validateAccess = useCallback(() => {
    if (loginType !== 'admin' && loginType !== 'master') {
      throw new Error('Only admin and master users can access event images');
    }
  }, [loginType]);

  // ------------------------------------------------------------------
  // GET REQUESTS (READ)
  // ------------------------------------------------------------------

  /**
   * Get all images for a specific event
   * @param {string} eventId - The event ID
   * @returns {Promise<Object>} - Response with images array and total count
   */
  const getEventImages = useCallback(async (eventId) => {
    validateAccess();
    setLoading(true);
    setError(null);

    try {
      const baseUrl = getEventImagesBaseUrl();
      const response = await makeAuthenticatedRequest(
        `${baseUrl}/${encodeURIComponent(eventId)}/images`
      );
      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to fetch event images';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getEventImagesBaseUrl]);

  /**
   * Check if an event has any images
   * @param {string} eventId - The event ID
   * @returns {Promise<boolean>} - True if event has images
   */
  const hasEventImages = useCallback(async (eventId) => {
    validateAccess();
    try {
      const result = await getEventImages(eventId);
      return result.total > 0;
    } catch (err) {
      return false;
    }
  }, [getEventImages, validateAccess]);

  /**
   * Get the primary (first) image for an event
   * @param {string} eventId - The event ID
   * @returns {Promise<Object|null>} - First image or null
   */
  const getPrimaryEventImage = useCallback(async (eventId) => {
    validateAccess();
    try {
      const result = await getEventImages(eventId);
      return result.images && result.images.length > 0 ? result.images[0] : null;
    } catch (err) {
      const errorMessage = err.message || 'Failed to get primary event image';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  }, [getEventImages, validateAccess]);

  // ------------------------------------------------------------------
  // POST REQUESTS (CREATE)
  // ------------------------------------------------------------------

  /**
   * Upload a single image to an event's gallery
   * @param {string} eventId - The event ID
   * @param {File} imageFile - The image file to upload
   * @returns {Promise<Object>} - Uploaded image data
   */
  const uploadEventImage = useCallback(async (eventId, imageFile) => {
    validateAccess();
    setLoading(true);
    setError(null);
    setUploadProgress(0);

    try {
      // Validate file type
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
      if (!validTypes.includes(imageFile.type)) {
        throw new Error('Invalid file type. Allowed: JPEG, PNG, GIF, WebP');
      }

      // Validate file size (15MB limit)
      const maxSize = 15 * 1024 * 1024; // 15MB
      if (imageFile.size > maxSize) {
        throw new Error('File size exceeds 15MB limit');
      }

      const formData = new FormData();
      formData.append('image', imageFile);

      const baseUrl = getEventImagesBaseUrl();
      const response = await makeAuthenticatedRequest(
        `${baseUrl}/${encodeURIComponent(eventId)}/images`,
        {
          method: 'POST',
          body: formData,
          headers: {
            'Content-Type': undefined // Let browser set content-type with boundary
          }
        }
      );

      setUploadProgress(100);
      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to upload event image';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
      setUploadProgress(0);
    }
  }, [makeAuthenticatedRequest, validateAccess, getEventImagesBaseUrl]);

  /**
   * Upload multiple images to an event's gallery
   * @param {string} eventId - The event ID
   * @param {File[]} imageFiles - Array of image files to upload
   * @returns {Promise<Object>} - Object with successful and failed uploads
   */
  const uploadMultipleEventImages = useCallback(async (eventId, imageFiles) => {
    validateAccess();
    setLoading(true);
    setError(null);

    const results = {
      successful: [],
      failed: []
    };

    try {
      for (let i = 0; i < imageFiles.length; i++) {
        const file = imageFiles[i];
        setUploadProgress(Math.round(((i) / imageFiles.length) * 100));

        try {
          const result = await uploadEventImage(eventId, file);
          results.successful.push({
            filename: file.name,
            result
          });
        } catch (err) {
          results.failed.push({
            filename: file.name,
            error: err.message
          });
        }
      }

      setUploadProgress(100);
      return results;
    } finally {
      setLoading(false);
      setUploadProgress(0);
    }
  }, [uploadEventImage, validateAccess]);

  // ------------------------------------------------------------------
  // UPDATE REQUESTS
  // ------------------------------------------------------------------

  /**
   * Reorder all images for an event
   * @param {string} eventId - The event ID
   * @param {string[]} imageIds - Array of Cloudflare image IDs in desired order
   * @returns {Promise<Object>} - Success message
   */
  const reorderEventImages = useCallback(async (eventId, imageIds) => {
    validateAccess();
    setLoading(true);
    setError(null);

    try {
      if (!Array.isArray(imageIds) || imageIds.length === 0) {
        throw new Error('No images provided for reordering');
      }

      const baseUrl = getEventImagesBaseUrl();
      const response = await makeAuthenticatedRequest(
        `${baseUrl}/${encodeURIComponent(eventId)}/images/reorder`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ image_ids: imageIds })
        }
      );

      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to reorder event images';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getEventImagesBaseUrl]);

  /**
   * Move an image to a specific position in the event gallery
   * @param {string} eventId - The event ID
   * @param {string} imageId - The Cloudflare image ID to move
   * @param {number} newPosition - New position (1-based index)
   * @returns {Promise<Object>} - Success message
   */
  const moveEventImageToPosition = useCallback(async (eventId, imageId, newPosition) => {
    validateAccess();

    try {
      const { images } = await getEventImages(eventId);

      if (!images || images.length === 0) {
        throw new Error('No images found');
      }

      const imageIndex = images.findIndex(img => img.cloudflare_image_id === imageId);
      if (imageIndex === -1) {
        throw new Error('Image not found');
      }

      const reorderedImages = [...images];
      const [movedImage] = reorderedImages.splice(imageIndex, 1);
      reorderedImages.splice(newPosition - 1, 0, movedImage);

      const imageIdOrder = reorderedImages.map(img => img.cloudflare_image_id);

      return await reorderEventImages(eventId, imageIdOrder);
    } catch (err) {
      const errorMessage = err.message || 'Failed to move event image';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  }, [getEventImages, reorderEventImages, validateAccess]);

  /**
   * Swap positions of two images in the event gallery
   * @param {string} eventId - The event ID
   * @param {string} imageId1 - First Cloudflare image ID
   * @param {string} imageId2 - Second Cloudflare image ID
   * @returns {Promise<Object>} - Success message
   */
  const swapEventImagePositions = useCallback(async (eventId, imageId1, imageId2) => {
    validateAccess();

    try {
      const { images } = await getEventImages(eventId);

      const image1Index = images.findIndex(img => img.cloudflare_image_id === imageId1);
      const image2Index = images.findIndex(img => img.cloudflare_image_id === imageId2);

      if (image1Index === -1 || image2Index === -1) {
        throw new Error('One or both images not found');
      }

      const reorderedImages = [...images];
      [reorderedImages[image1Index], reorderedImages[image2Index]] = 
        [reorderedImages[image2Index], reorderedImages[image1Index]];

      const imageIdOrder = reorderedImages.map(img => img.cloudflare_image_id);

      return await reorderEventImages(eventId, imageIdOrder);
    } catch (err) {
      const errorMessage = err.message || 'Failed to swap event image positions';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  }, [getEventImages, reorderEventImages, validateAccess]);

  /**
   * Set an image as the primary (first) image for an event
   * @param {string} eventId - The event ID
   * @param {string} imageId - The Cloudflare image ID to set as primary
   * @returns {Promise<Object>} - Success message
   */
  const setAsPrimaryEventImage = useCallback(async (eventId, imageId) => {
    return await moveEventImageToPosition(eventId, imageId, 1);
  }, [moveEventImageToPosition]);

  // ------------------------------------------------------------------
  // DELETE REQUESTS
  // ------------------------------------------------------------------

  /**
   * Delete a single image from an event's gallery
   * @param {string} eventId - The event ID
   * @param {string} imageId - The Cloudflare image ID to delete
   * @returns {Promise<void>}
   */
  const deleteEventImage = useCallback(async (eventId, imageId) => {
    validateAccess();
    setLoading(true);
    setError(null);

    try {
      const baseUrl = getEventImagesBaseUrl();
      const response = await makeAuthenticatedRequest(
        `${baseUrl}/${encodeURIComponent(eventId)}/images/${encodeURIComponent(imageId)}`,
        {
          method: 'DELETE'
        }
      );

      // 204 No Content doesn't have a body to parse
      if (response.status !== 204) {
        await handleApiResponse(response);
      }
    } catch (err) {
      const errorMessage = err.message || 'Failed to delete event image';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getEventImagesBaseUrl]);

  /**
   * Delete multiple images from an event's gallery
   * @param {string} eventId - The event ID
   * @param {string[]} imageIds - Array of Cloudflare image IDs to delete
   * @returns {Promise<Object>} - Object with successful and failed deletions
   */
  const deleteMultipleEventImages = useCallback(async (eventId, imageIds) => {
    validateAccess();
    setLoading(true);
    setError(null);

    const results = {
      successful: [],
      failed: []
    };

    try {
      for (const imageId of imageIds) {
        try {
          await deleteEventImage(eventId, imageId);
          results.successful.push(imageId);
        } catch (err) {
          results.failed.push({
            imageId,
            error: err.message
          });
        }
      }

      return results;
    } finally {
      setLoading(false);
    }
  }, [deleteEventImage, validateAccess]);

  // ------------------------------------------------------------------
  // UTILITIES
  // ------------------------------------------------------------------

  /**
   * Format file size from bytes to human-readable format
   * @param {number} bytes - File size in bytes
   * @returns {string} - Formatted file size
   */
  const formatFileSize = useCallback((bytes) => {
    if (!bytes || bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }, []);

  /**
   * Get endpoint information for debugging
   * @returns {Object} - Endpoint configuration info
   */
  const getEndpointInfo = useCallback(() => {
    return {
      loginType,
      baseUrl: getEventImagesBaseUrl(),
      accessLevel: loginType === 'master' ? 'Master Access' : 'Admin Access',
      requiredHeaders: loginType === 'master'
        ? ['Authorization', 'secret_credential']
        : ['Authorization', 'admin_id'],
    };
  }, [loginType, getEventImagesBaseUrl]);

  /**
   * Get the Cloudflare delivery URL for an image
   * @param {string} cloudflareImageId - The Cloudflare image ID
   * @param {string} variant - The variant to use (default: 'public')
   * @returns {string} - Full Cloudflare delivery URL
   */
  const getCloudflareImageUrl = useCallback((cloudflareImageId, variant = 'public') => {
    const accountHash = process.env.REACT_APP_CLOUDFLARE_ACCOUNT_HASH;
    if (!accountHash) {
      console.warn('REACT_APP_CLOUDFLARE_ACCOUNT_HASH not set');
      return '';
    }
    return `https://imagedelivery.net/${accountHash}/${cloudflareImageId}/${variant}`;
  }, []);

  // ------------------------------------------------------------------
  // MEMOIZED CONTEXT VALUE (Prevents Infinite Loops)
  // ------------------------------------------------------------------
  const value = useMemo(() => ({
    // State
    loading,
    error,
    uploadProgress,

    // Read Operations
    getEventImages,
    hasEventImages,
    getPrimaryEventImage,

    // Create Operations
    uploadEventImage,
    uploadMultipleEventImages,

    // Update Operations
    reorderEventImages,
    moveEventImageToPosition,
    swapEventImagePositions,
    setAsPrimaryEventImage,

    // Delete Operations
    deleteEventImage,
    deleteMultipleEventImages,

    // Helpers
    clearError,
    formatFileSize,
    getEndpointInfo,
    getCloudflareImageUrl,

    // Computed values
    isAdminUser: loginType === 'admin',
    isMasterUser: loginType === 'master',
    hasEventImagesAccess: loginType === 'admin' || loginType === 'master',
    currentAccessLevel: loginType,
  }), [
    loading, error, uploadProgress, loginType,
    getEventImages, hasEventImages, getPrimaryEventImage,
    uploadEventImage, uploadMultipleEventImages,
    reorderEventImages, moveEventImageToPosition, swapEventImagePositions, setAsPrimaryEventImage,
    deleteEventImage, deleteMultipleEventImages,
    clearError, formatFileSize, getEndpointInfo, getCloudflareImageUrl
  ]);

  return (
    <EventImagesContext.Provider value={value}>
      {children}
    </EventImagesContext.Provider>
  );
};

export default EventImagesContext;